import { supabase } from "../lib/supabase";
import { filenameFromContentDisposition } from "./contentDisposition";
import { offerTermsForRequest } from "../components/config/commercialOffer";

const BASE_URL = process.env.REACT_APP_API_BASE?.replace(/\/$/, "");

async function authHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(await authHeaders()),
    ...(options.headers || {}),
  };

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  const text = await res.text(); // read once
  if (!res.ok) {
    const error = new Error(text || res.statusText);
    error.status = res.status;
    error.responseText = text;
    throw error;
  }

  // handle empty body (some DELETE endpoints return 204)
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}


function getFilenameFromDisposition(value, fallback) {
  // One parser for the whole app; see contentDisposition.js. This one took the
  // FIRST filename parameter, which is now the ASCII fallback, so a Greek name
  // would have downloaded under its transliterated stand-in.
  return filenameFromContentDisposition(value) || fallback;
}

async function requestBlob(path, options = {}, fallbackName = "download") {
  const headers = {
    ...(await authHeaders()),
    ...(options.headers || {}),
  };

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }

  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") || "";
  const filename = getFilenameFromDisposition(disposition, fallbackName);
  return { blob, filename };
}

export const metenApi = {
  getCatalogFilterFacets: () => request("/catalog/filter-facets"),
  getSizingReferenceModules: () => request('/catalog/modules'),
  listProjects: (q) => request(`/projects${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  // Id, name and dates only, for the Open dialog. Falls back to the full list
  // while a backend without this endpoint is still running (it reads
  // "summaries" as a project id and answers 422 or 404).
  listProjectSummaries: async () => {
    try {
      return await request(`/projects/summaries`);
    } catch (error) {
      if (error?.status === 404 || error?.status === 422) return request(`/projects`);
      throw error;
    }
  },
  getProfile: () => request("/profiles/me"),
  completePasswordChange: () => request("/profiles/me/password-change-complete", { method: "POST" }),
  getDemoProfile: () => request("/profiles/me/demo"),
  startPvSizingJob: (projectId, body) =>
    request(`/pv-sizing/${encodeURIComponent(projectId)}/jobs`, { method: "POST", body: JSON.stringify(body) }),
  continuePvSizingJob: (projectId, jobId, body) =>
    request(`/pv-sizing/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(jobId)}/continue`, { method: "POST", body: JSON.stringify(body) }),
  getPvSizingLatestJob: (projectId) =>
    request(`/pv-sizing/${encodeURIComponent(projectId)}/jobs/latest?cb=${Date.now()}`, { cache: "no-store" }),
  cancelPvSizingJob: (projectId, jobId) =>
    request(`/pv-sizing/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(jobId)}/cancel`, { method: "POST" }),
  getPvSizingStreamTicket: (projectId, jobId) =>
    request(`/pv-sizing/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(jobId)}/stream-ticket`, { method: "POST" }),
  pvSizingStreamUrl: (projectId, jobId, ticket) =>
    `${BASE_URL}/pv-sizing/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(jobId)}/stream?stream_ticket=${encodeURIComponent(ticket)}`,
  // `language` is the project's report language: the download follows it even
  // when the run was stored in the other one.
  downloadPvSizingReport: (projectId, jobId, { language } = {}) =>
    requestBlob(
      `/pv-sizing/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(jobId)}/report?cb=${Date.now()}${language ? `&language=${language}` : ""}`,
      { method: "GET" },
      `pv_sizing_${String(jobId).slice(0, 8)}.pdf`
    ),
  downloadPvSizingClientReport: (projectId, jobId, { language } = {}) =>
    requestBlob(
      `/pv-sizing/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(jobId)}/report/client?cb=${Date.now()}${language ? `&language=${language}` : ""}`,
      { method: "GET" },
      `pv_sizing_client_${String(jobId).slice(0, 8)}.pdf`
    ),
  downloadPvSizingTables: (projectId, jobId, { language } = {}) =>
    requestBlob(
      `/pv-sizing/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(jobId)}/export.xlsx?cb=${Date.now()}${language ? `&language=${language}` : ""}`,
      { method: "GET" },
      `pv_sizing_${String(jobId).slice(0, 8)}.xlsx`
    ),
  updateProfile: (payload) => request("/profiles/me", { method: "PUT", body: JSON.stringify(payload) }),
  listUsers: () => request("/admin/users"),
  getUserMonitoring: (options = {}) => request("/admin/users/monitoring", { ...options, cache: "no-store" }),
  getSystemStatus: () => request("/system/status"),
  listAnnouncements: () => request("/announcements", { cache: "no-store" }),
  markAnnouncementRead: (id) =>
    request(`/announcements/${encodeURIComponent(id)}/read`, { method: "POST" }),
  dismissAnnouncement: (id) =>
    request(`/announcements/${encodeURIComponent(id)}`, { method: "DELETE" }),
  adminListAnnouncements: () => request("/admin/announcements", { cache: "no-store" }),
  publishAnnouncement: (payload) =>
    request("/admin/announcements", { method: "POST", body: JSON.stringify(payload) }),
  withdrawAnnouncement: (id) =>
    request(`/admin/announcements/${encodeURIComponent(id)}/withdraw`, { method: "POST" }),
  setMaintenanceMode: (maintenanceMode, maintenanceMessage = null) =>
    request("/system/maintenance", {
      method: "PATCH",
      body: JSON.stringify({
        maintenance_mode: maintenanceMode,
        maintenance_message: maintenanceMessage,
      }),
    }),
  updateUserProfile: (userId, payload) =>
    request(`/admin/users/${encodeURIComponent(String(userId))}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  updateUserDemoSettings: (userId, payload) =>
    request(`/admin/users/${encodeURIComponent(String(userId))}/demo`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  updateUserAccess: (userId, payload) =>
    request(`/admin/users/${encodeURIComponent(String(userId))}/access`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  getUserAccessHistory: (userId) =>
    request(`/admin/users/${encodeURIComponent(String(userId))}/access/history`),
  getProject: (id) => request(`/projects/${id}`),
  createProject: (body) => request(`/projects`, { method: "POST", body: JSON.stringify(body) }),
  updateProject: (id, body) => request(`/projects/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  // Fetch configurations for a project (API uses /configs/by_project/{project_id})
  listConfigsByProject: async (projectId, q) => {
    const path = `/configs/by_project/${projectId}${q ? `?q=${encodeURIComponent(q)}` : ""}`;
    try {
      return await request(path);
    } catch (err) {
      // As a fallback, try the generic list and filter client-side if exposed
      try {
        const all = await request("/configs");
        if (Array.isArray(all)) {
          return all.filter((c) => String(c.project_id) === String(projectId));
        }
      } catch (_) {
        // ignore
      }
      throw err;
    }
  },

  getConfig: (configId) => request(`/configs/${configId}`),
  createConfig: (body) => request(`/configs`, { method: "POST", body: JSON.stringify(body) }),
  updateConfig: (configId, body) =>
    request(`/configs/${configId}`, { method: "PATCH", body: JSON.stringify(body) }),


  deleteProject: (projectId) => request(`/projects/${projectId}`, { method: "DELETE" }),

  deleteConfig: (configId) => request(`/configs/${configId}`, { method: "DELETE" }),

  runFeasibilitySimulation: (body) =>
    request(`/feasibility/run_simulation`, { method: "POST", body: JSON.stringify(body) }),
  cancelFeasibilitySimulation: (body) =>
    request(`/feasibility/cancel_simulation`, { method: "POST", body: JSON.stringify(body) }),
  getFeasibilityStatus: (projectId) =>
    request(`/feasibility/${projectId}/status`, { method: "GET" }),

  // Virtual net billing: the allocation percentages that net the most energy
  // (read-only; nothing is stored and no study is counted).
  suggestVirtualNetBillingAllocation: (projectId, body) =>
    request(`/feasibility/virtual-net-billing/${encodeURIComponent(projectId)}/allocation`, { method: "POST", body: JSON.stringify(body) }),

  // Battery comparison (Behind-the-Meter): check before starting, start (one
  // study against the daily limit), follow, cancel, and the comparison PDF.
  checkBatteryComparison: (projectId, body) =>
    request(`/feasibility/battery-comparison/${encodeURIComponent(projectId)}/check`, { method: "POST", body: JSON.stringify(body) }),
  startBatteryComparison: (projectId, body) =>
    request(`/feasibility/battery-comparison/${encodeURIComponent(projectId)}`, { method: "POST", body: JSON.stringify(body) }),
  getBatteryComparison: (projectId) =>
    request(`/feasibility/battery-comparison/${encodeURIComponent(projectId)}?cb=${Date.now()}`, { method: "GET" }),
  cancelBatteryComparison: (projectId) =>
    request(`/feasibility/battery-comparison/${encodeURIComponent(projectId)}/cancel`, { method: "POST" }),
  downloadBatteryComparison: (projectId, { language } = {}) =>
    requestBlob(
      `/feasibility/battery-comparison/${encodeURIComponent(projectId)}/pdf?cb=${Date.now()}${language ? `&language=${language}` : ""}`,
      { method: "GET" },
      `battery_comparison_${projectId}.pdf`
    ),

  // cb=Date.now(): these download URLs are constant per project while their
  // content changes on every simulation run — edge caches were serving stale
  // proposals/teasers. Unique query param guarantees a cache miss; the
  // backend additionally responds with Cache-Control: no-store.
  downloadFeasibilityProposal: (projectId) =>
    requestBlob(
      `/feasibility/proposal/${projectId}/download?cb=${Date.now()}`,
      { method: "GET" },
      `feasibility_proposal_${projectId}.pdf`
    ),
  downloadCommercialOffer: (projectId, terms, { kind = "offer", appendix = false, format = "pdf" } = {}) =>
    requestBlob(
      `/feasibility/commercial-offer/${encodeURIComponent(projectId)}/download?cb=${Date.now()}${["benefit", "proposal"].includes(kind) ? `&kind=${kind}` : ""}${appendix ? "&appendix=true" : ""}${format === "docx" ? "&format=docx" : ""}`,
      terms
        ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(offerTermsForRequest(terms)) }
        : { method: "GET" },
      `${kind === "proposal" ? "commercial_proposal" : kind === "benefit" ? "economic_benefit" : "commercial_offer"}_${projectId}.${format === "docx" ? "docx" : "pdf"}`
    ),
  // The headline figures of the detailed commercial proposal, for the results
  // card: the same frozen study and the same terms as its PDF.
  getCommercialOfferSummary: (projectId, terms) =>
    request(`/feasibility/commercial-offer/${encodeURIComponent(projectId)}/summary`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(offerTermsForRequest(terms)),
    }),
  // kind: "standard" (one-pager) or "detailed" (charts + EL/EN page).
  downloadFeasibilityTeaser: (projectId, { kind = "standard" } = {}) =>
    requestBlob(
      `/feasibility/teaser/${projectId}/download?cb=${Date.now()}&kind=${encodeURIComponent(kind)}`,
      { method: "GET" },
      kind === "detailed"
        ? `feasibility_teaser_detailed_${projectId}.html`
        : `feasibility_teaser_${projectId}.html`
    ),
  // snapshot=true (default): every publish mints a NEW public URL frozen to
  // the current run — safe to send to clients, later runs can't change it.
  // Pass { snapshot: false } to refresh the project's stable "live" link
  // (always shows the latest completed run) instead.
  publishFeasibilityTeaser: (projectId, { snapshot = true, kind = "standard" } = {}) =>
    request(
      `/feasibility/teaser/${projectId}/publish?snapshot=${snapshot ? "true" : "false"}&kind=${encodeURIComponent(kind)}`,
      {
        method: "POST",
      }
    ),
  getFeasibilityTeaserStats: (projectId, { kind = "standard" } = {}) =>
    request(`/feasibility/teaser/${projectId}/stats?kind=${encodeURIComponent(kind)}`, {
      method: "GET",
    }),
  // Revoke this project's public teaser link(s). Idempotent; the link then
  // returns 410 to visitors. Publishing again reactivates the live link.
  revokeFeasibilityTeaser: (projectId) =>
    request(`/feasibility/teaser/${projectId}/revoke`, { method: "POST" }),
  // Encrypted .pythia project package (server-side AES-GCM; auth required).
  exportProjectPackage: (payload, fallbackName = "project.pythia") =>
    requestBlob(
      `/projects/package/export`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
      fallbackName
    ),
  importProjectPackage: (fileBlob) =>
    request(`/projects/package/import`, {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: fileBlob,
    }),
  generateFeasibilityReport: (projectId, body) =>
    request(`/feasibility/report/${projectId}`, {
      method: "POST",
      ...(body ? { body: JSON.stringify(body) } : {}),
    }),
  downloadFeasibilityReport: (projectId) =>
    requestBlob(
      `/feasibility/report/${projectId}/download?cb=${Date.now()}`,
      { method: "GET" },
      `feasibility_report_${projectId}.pdf`
    ),

};
